import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesktopShell } from '../components/DesktopShell';
import { TopBar } from '../components/TopBar';
import { Card } from '../components/Card';
import { Btn } from '../components/Btn';
import { Ic } from '../components/Icon';
import { matchesApi } from '../api/matches';
import type { MatchPhase, MatchStatus, Team } from '../api/types';
import type { HttpError } from '../api/client';

type SlotMode = 'team' | 'group' | 'match';
interface TeamSlot {
  mode: SlotMode;
  team: string;        // 3-letter team code (used when mode='team')
  placeholder: string; // 1A / 2B / 3ABCDF / W73 / RU101 (used when mode≠'team')
}

const GROUP_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'] as const;

// 24 group qualifiers + 8 best-3rd slots used in R32 (FIFA mapping)
const GROUP_PLACEHOLDERS: { v: string; l: string }[] = [
  ...GROUP_LETTERS.flatMap((g) => [
    { v: `1${g}`, l: `1${g} — zwycięzca grupy ${g}` },
    { v: `2${g}`, l: `2${g} — drugie miejsce w grupie ${g}` },
  ]),
  { v: '3ABCDF', l: '3ABCDF — najlepszy 3. (z grup A/B/C/D/F)' },
  { v: '3CDFGH', l: '3CDFGH — najlepszy 3. (z grup C/D/F/G/H)' },
  { v: '3CEFHI', l: '3CEFHI — najlepszy 3. (z grup C/E/F/H/I)' },
  { v: '3EHIJK', l: '3EHIJK — najlepszy 3. (z grup E/H/I/J/K)' },
  { v: '3AEHIJ', l: '3AEHIJ — najlepszy 3. (z grup A/E/H/I/J)' },
  { v: '3BEFIJ', l: '3BEFIJ — najlepszy 3. (z grup B/E/F/I/J)' },
  { v: '3EFGIJ', l: '3EFGIJ — najlepszy 3. (z grup E/F/G/I/J)' },
  { v: '3DEIJL', l: '3DEIJL — najlepszy 3. (z grup D/E/I/J/L)' },
];

// Possible W{n}/RU{n} sources: M73-M88 → R16, M89-M96 → QF, M97-M100 → SF, M101-M102 → 3rd/Final
const MATCH_PLACEHOLDERS: { v: string; l: string }[] = [
  ...Array.from({ length: 16 }, (_, i) => 73 + i).map((n) => ({ v: `W${n}`, l: `W${n} — zwycięzca meczu ${n}` })),
  ...Array.from({ length: 8 }, (_, i) => 89 + i).map((n) => ({ v: `W${n}`, l: `W${n} — zwycięzca meczu ${n}` })),
  ...Array.from({ length: 4 }, (_, i) => 97 + i).map((n) => ({ v: `W${n}`, l: `W${n} — zwycięzca meczu ${n}` })),
  { v: 'W101', l: 'W101 — zwycięzca półfinału 1' },
  { v: 'W102', l: 'W102 — zwycięzca półfinału 2' },
  { v: 'RU101', l: 'RU101 — przegrany półfinału 1' },
  { v: 'RU102', l: 'RU102 — przegrany półfinału 2' },
];

const GROUP_PREFIX = /^[12][A-L]$|^3[A-L]{4,6}$/;
const MATCH_PREFIX = /^(W|RU)\d{1,3}$/;

function toLocalInputValue(iso: string): string {
  // Backend przechowuje kickoff w UTC; <input type="datetime-local"> interpretuje wartość
  // jako lokalny czas, więc konwertujemy ISO → YYYY-MM-DDTHH:mm w strefie przeglądarki.
  const d = new Date(iso);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function slotFromMatch(team: string, placeholder: string | null): TeamSlot {
  if (!placeholder) return { mode: 'team', team, placeholder: '' };
  if (GROUP_PREFIX.test(placeholder)) return { mode: 'group', team: 'TBA', placeholder };
  if (MATCH_PREFIX.test(placeholder)) return { mode: 'match', team: 'TBA', placeholder };
  return { mode: 'team', team, placeholder: '' };
}

function resolveSlot(slot: TeamSlot): { team: string; placeholder: string | null } {
  if (slot.mode === 'team') return { team: slot.team, placeholder: null };
  return { team: 'TBA', placeholder: slot.placeholder || null };
}

const PHASES: { v: MatchPhase; l: string }[] = [
  { v: 'GROUP', l: 'Faza grupowa' },
  { v: 'ROUND_OF_32', l: '1/16 finału' },
  { v: 'ROUND_OF_16', l: '1/8 finału' },
  { v: 'QUARTER_FINAL', l: 'Ćwierćfinały' },
  { v: 'SEMI_FINAL', l: 'Półfinały' },
  { v: 'THIRD_PLACE', l: 'Mecz o 3. miejsce' },
  { v: 'FINAL', l: 'Finał' },
];

const STATUSES: { v: MatchStatus; l: string }[] = [
  { v: 'PENDING_DRAW', l: 'Oczekuje na drużyny' },
  { v: 'UPCOMING', l: 'Planowany' },
  { v: 'LIVE', l: 'Na żywo' },
  { v: 'FINISHED', l: 'Zakończony' },
];

export function AdminEditMatchPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const teams = useQuery({ queryKey: ['teams'], queryFn: matchesApi.teams });
  const existing = useQuery({
    queryKey: ['match', id],
    queryFn: () => matchesApi.detail(id!),
    enabled: !isNew && !!id,
  });

  const [phase, setPhase] = useState<MatchPhase>('GROUP');
  const [group, setGroup] = useState('A');
  const [homeSlot, setHomeSlot] = useState<TeamSlot>({ mode: 'team', team: '', placeholder: '' });
  const [awaySlot, setAwaySlot] = useState<TeamSlot>({ mode: 'team', team: '', placeholder: '' });
  const [kickoff, setKickoff] = useState('');
  const [venue, setVenue] = useState('');
  const [status, setStatus] = useState<MatchStatus>('UPCOMING');
  const [homeScore, setHomeScore] = useState<number | ''>('');
  const [awayScore, setAwayScore] = useState<number | ''>('');
  const [hasExtraTime, setHasExtraTime] = useState(false);
  const [extraTimeHomeScore, setExtraTimeHomeScore] = useState<number | ''>('');
  const [extraTimeAwayScore, setExtraTimeAwayScore] = useState<number | ''>('');
  const [hasPenalties, setHasPenalties] = useState(false);
  const [penaltyHomeScore, setPenaltyHomeScore] = useState<number | ''>('');
  const [penaltyAwayScore, setPenaltyAwayScore] = useState<number | ''>('');
  const [externalMatchId, setExternalMatchId] = useState<number | ''>('');

  useEffect(() => {
    if (existing.data) {
      const m = existing.data;
      setPhase(m.phase);
      setGroup(m.group ?? '');
      setHomeSlot(slotFromMatch(m.homeTeam, m.homePlaceholder));
      setAwaySlot(slotFromMatch(m.awayTeam, m.awayPlaceholder));
      setKickoff(toLocalInputValue(m.kickoff));
      setVenue(m.venue ?? '');
      setStatus(m.status);
      setHomeScore(m.homeScore ?? '');
      setAwayScore(m.awayScore ?? '');
      const et = m.extraTimeHomeScore != null && m.extraTimeAwayScore != null;
      setHasExtraTime(et);
      setExtraTimeHomeScore(m.extraTimeHomeScore ?? '');
      setExtraTimeAwayScore(m.extraTimeAwayScore ?? '');
      const pens = m.penaltyHomeScore != null && m.penaltyAwayScore != null;
      setHasPenalties(pens);
      setPenaltyHomeScore(m.penaltyHomeScore ?? '');
      setPenaltyAwayScore(m.penaltyAwayScore ?? '');
      setExternalMatchId(m.externalMatchId ?? '');
    }
  }, [existing.data]);

  const save = useMutation({
    mutationFn: () => {
      const home = resolveSlot(homeSlot);
      const away = resolveSlot(awaySlot);
      const payload = {
        phase,
        group: phase === 'GROUP' ? group : null,
        homeTeam: home.team,
        awayTeam: away.team,
        homePlaceholder: home.placeholder,
        awayPlaceholder: away.placeholder,
        kickoff: new Date(kickoff).toISOString(),
        venue: venue || null,
        status,
        homeScore: homeScore === '' ? null : Number(homeScore),
        awayScore: awayScore === '' ? null : Number(awayScore),
        extraTimeHomeScore: hasExtraTime && extraTimeHomeScore !== '' ? Number(extraTimeHomeScore) : null,
        extraTimeAwayScore: hasExtraTime && extraTimeAwayScore !== '' ? Number(extraTimeAwayScore) : null,
        penaltyHomeScore: hasExtraTime && hasPenalties && penaltyHomeScore !== '' ? Number(penaltyHomeScore) : null,
        penaltyAwayScore: hasExtraTime && hasPenalties && penaltyAwayScore !== '' ? Number(penaltyAwayScore) : null,
        externalMatchId: externalMatchId === '' ? null : Number(externalMatchId),
      };
      return isNew ? matchesApi.create(payload) : matchesApi.update(id!, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['matches'] });
      queryClient.invalidateQueries({ queryKey: ['my-bets'] });
      navigate('/admin/matches');
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <DesktopShell>
      <TopBar
        title={isNew ? 'Nowy mecz' : 'Edycja meczu'}
        subtitle={isNew ? 'Dodaj mecz do turnieju' : undefined}
      />
      <form onSubmit={onSubmit} style={{ flex: 1, padding: '24px 36px 40px', maxWidth: 720 }}>
        <Card padding={24}>
          <Row>
            <Field label="Data i godzina" wide>
              <input
                type="datetime-local"
                required
                value={kickoff}
                onChange={(e) => setKickoff(e.target.value)}
                style={inputStyle}
              />
            </Field>
          </Row>

          <Row>
            <Field label="Faza">
              <select value={phase} onChange={(e) => setPhase(e.target.value as MatchPhase)} style={inputStyle}>
                {PHASES.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
              </select>
            </Field>
            {phase === 'GROUP' && (
              <Field label="Grupa">
                <select value={group} onChange={(e) => setGroup(e.target.value)} style={inputStyle}>
                  {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </Field>
            )}
            <Field label="Status">
              <select value={status} onChange={(e) => setStatus(e.target.value as MatchStatus)} style={inputStyle}>
                {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
              </select>
            </Field>
          </Row>

          <Row>
            <Field label="Gospodarze">
              <SlotPicker slot={homeSlot} onChange={setHomeSlot} teams={teams.data ?? []} />
            </Field>
            <Field label="Goście">
              <SlotPicker slot={awaySlot} onChange={setAwaySlot} teams={teams.data ?? []} />
            </Field>
          </Row>

          <Row>
            <Field label="Obiekt" wide>
              <input
                value={venue}
                onChange={(e) => setVenue(e.target.value)}
                placeholder="Estadio Azteca, Mexico City"
                style={inputStyle}
              />
            </Field>
          </Row>

          <Row>
            <Field label="ID meczu (football-data.org)" wide>
              <input
                type="number"
                value={externalMatchId}
                onChange={(e) => setExternalMatchId(e.target.value === '' ? '' : Number(e.target.value))}
                placeholder="np. 537327 — automatyczny sync statusu i wyniku"
                style={inputStyle}
              />
            </Field>
          </Row>

          <div
            style={{
              padding: 16,
              background: 'rgba(244,185,58,.08)',
              borderRadius: 10,
              border: '1px solid rgba(244,185,58,.25)',
              marginTop: 4,
            }}
          >
            <div
              style={{
                fontSize: 11,
                color: '#6b4a00',
                fontWeight: 600,
                letterSpacing: '.06em',
                marginBottom: 8,
              }}
            >
              WYNIK KOŃCOWY (90')
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 12, alignItems: 'center' }}>
              <input
                type="number"
                min={0}
                max={50}
                value={homeScore}
                onChange={(e) => setHomeScore(e.target.value === '' ? '' : Number(e.target.value))}
                className="mono serif"
                style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
              />
              <div className="serif" style={{ textAlign: 'center', fontSize: 24, color: '#5b6578' }}>:</div>
              <input
                type="number"
                min={0}
                max={50}
                value={awayScore}
                onChange={(e) => setAwayScore(e.target.value === '' ? '' : Number(e.target.value))}
                className="mono serif"
                style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
              />
            </div>
            <div style={{ fontSize: 11, color: '#6b4a00', marginTop: 8 }}>
              Ustawienie wyniku + status "Zakończony" automatycznie przeliczy punkty wszystkich graczy.
            </div>
          </div>

          {phase !== 'GROUP' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={hasExtraTime}
                onChange={(e) => {
                  const next = e.target.checked;
                  setHasExtraTime(next);
                  if (!next) {
                    setExtraTimeHomeScore('');
                    setExtraTimeAwayScore('');
                    setHasPenalties(false);
                    setPenaltyHomeScore('');
                    setPenaltyAwayScore('');
                  }
                }}
              />
              <span>Mecz przeszedł do dogrywki</span>
            </label>
          )}

          {phase !== 'GROUP' && hasExtraTime && (
            <div
              style={{
                padding: 16,
                background: 'rgba(244,185,58,.08)',
                borderRadius: 10,
                border: '1px solid rgba(244,185,58,.25)',
                marginTop: 8,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  color: '#6b4a00',
                  fontWeight: 600,
                  letterSpacing: '.06em',
                  marginBottom: 8,
                }}
              >
                WYNIK PO DOGRYWCE (120')
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 12, alignItems: 'center' }}>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={extraTimeHomeScore}
                  onChange={(e) => setExtraTimeHomeScore(e.target.value === '' ? '' : Number(e.target.value))}
                  className="mono serif"
                  style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
                />
                <div className="serif" style={{ textAlign: 'center', fontSize: 24, color: '#5b6578' }}>:</div>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={extraTimeAwayScore}
                  onChange={(e) => setExtraTimeAwayScore(e.target.value === '' ? '' : Number(e.target.value))}
                  className="mono serif"
                  style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
                />
              </div>
              <div style={{ fontSize: 11, color: '#6b4a00', marginTop: 8 }}>
                Wynik po 120 minutach (przed ewentualnymi karnymi). Nie wpływa na punktację — punkty wciąż liczone z wyniku po 90'.
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 13, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={hasPenalties}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setHasPenalties(next);
                    if (!next) {
                      setPenaltyHomeScore('');
                      setPenaltyAwayScore('');
                    }
                  }}
                />
                <span>Rozstrzygnięty karnymi</span>
              </label>

              {hasPenalties && (
                <div style={{ marginTop: 10 }}>
                  <div
                    style={{
                      fontSize: 11,
                      color: '#6b4a00',
                      fontWeight: 600,
                      letterSpacing: '.06em',
                      marginBottom: 8,
                    }}
                  >
                    RZUTY KARNE
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 40px 1fr', gap: 12, alignItems: 'center' }}>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={penaltyHomeScore}
                      onChange={(e) => setPenaltyHomeScore(e.target.value === '' ? '' : Number(e.target.value))}
                      className="mono serif"
                      style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
                    />
                    <div className="serif" style={{ textAlign: 'center', fontSize: 24, color: '#5b6578' }}>:</div>
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={penaltyAwayScore}
                      onChange={(e) => setPenaltyAwayScore(e.target.value === '' ? '' : Number(e.target.value))}
                      className="mono serif"
                      style={{ ...inputStyle, fontSize: 28, height: 60, textAlign: 'center', fontWeight: 700 }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {save.isError && (
            <div style={{ marginTop: 14, padding: 12, background: 'rgba(208,70,60,.08)', color: '#8a2a22', borderRadius: 10, fontSize: 13 }}>
              {(save.error as HttpError).body?.message ?? 'Zapisywanie nie powiodło się.'}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 24 }}>
            <Btn type="button" variant="ghost" onClick={() => navigate('/admin/matches')}>
              Anuluj
            </Btn>
            <Btn type="submit" variant="accent" icon={<Ic.check size={14} />} disabled={save.isPending}>
              {save.isPending ? 'Zapisywanie…' : 'Zapisz zmiany'}
            </Btn>
          </div>
        </Card>
      </form>
    </DesktopShell>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
      {children}
    </div>
  );
}

function Field({ label, children, wide }: { label: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : undefined }}>
      <div style={{ fontSize: 11, color: '#5b6578', fontWeight: 600, letterSpacing: '.06em', marginBottom: 5 }}>
        {label.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 40,
  borderRadius: 9,
  border: '1px solid rgba(15,30,61,.14)',
  padding: '0 12px',
  fontSize: 14,
  background: '#fff',
  outline: 'none',
  fontFamily: 'inherit',
};

function SlotPicker({
  slot,
  onChange,
  teams,
}: {
  slot: TeamSlot;
  onChange: (next: TeamSlot) => void;
  teams: Team[];
}) {
  const setMode = (mode: SlotMode) => {
    if (mode === slot.mode) return;
    if (mode === 'team') onChange({ mode, team: slot.team || '', placeholder: '' });
    else onChange({ mode, team: 'TBA', placeholder: '' });
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, marginBottom: 6, fontSize: 12, color: '#5b6578' }}>
        {[
          { v: 'team' as SlotMode, l: 'Drużyna' },
          { v: 'group' as SlotMode, l: 'Z grupy' },
          { v: 'match' as SlotMode, l: 'Z meczu' },
        ].map((opt) => (
          <label key={opt.v} style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}>
            <input
              type="radio"
              checked={slot.mode === opt.v}
              onChange={() => setMode(opt.v)}
              style={{ margin: 0 }}
            />
            {opt.l}
          </label>
        ))}
      </div>
      {slot.mode === 'team' && (
        <select
          value={slot.team}
          onChange={(e) => onChange({ ...slot, team: e.target.value })}
          required
          style={inputStyle}
        >
          <option value="">— wybierz —</option>
          {teams
            .filter((t) => t.code !== 'TBA')
            .map((t) => (
              <option key={t.code} value={t.code}>
                {t.name}
              </option>
            ))}
        </select>
      )}
      {slot.mode === 'group' && (
        <select
          value={slot.placeholder}
          onChange={(e) => onChange({ ...slot, placeholder: e.target.value })}
          required
          style={inputStyle}
        >
          <option value="">— wybierz —</option>
          {GROUP_PLACEHOLDERS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.l}
            </option>
          ))}
        </select>
      )}
      {slot.mode === 'match' && (
        <select
          value={slot.placeholder}
          onChange={(e) => onChange({ ...slot, placeholder: e.target.value })}
          required
          style={inputStyle}
        >
          <option value="">— wybierz —</option>
          {MATCH_PLACEHOLDERS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.l}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}
